/*
 * Vremou bestelknop
 * ---------------------------------------------------------------
 * Eén groene knop die de gekozen variant en hoeveelheid aan de
 * Shopify-winkelwagen toevoegt en daarna doorstuurt naar de
 * officiële Shopify Checkout.
 *
 * Bewuste keuzes:
 *   - /cart/add.js voegt toe en vervangt niet, dus producten die de
 *     bezoeker al in de winkelwagen had blijven staan.
 *   - Een vlag blokkeert dubbele verzending; de knop wordt tijdens het
 *     verwerken uitgeschakeld en toont "Bezig met bestellen…".
 *   - Komt de bezoeker met de terugknop terug uit Checkout, dan zet
 *     pageshow de knop weer vrij. Zonder dat blijft hij hangen in de
 *     verwerkstand en zou een tweede poging niets doen.
 *   - Het bedrag op de knop is prijs x aantal en wordt in de valuta van
 *     de winkel opgemaakt.
 */

class VremouBuy extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-vr-submit]');
    this.label = this.querySelector('[data-vr-submit-label]');
    this.errorEl = this.querySelector('[data-vr-error]');
    this.liveEl = this.querySelector('[data-vr-live]');
    this.quantityEl = this.querySelector('[data-vr-quantity]');
    if (!this.form || !this.button) return;

    this.busy = false;
    this.currency = this.dataset.currency || 'EUR';
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';

    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    if (this.quantityEl) {
      this.quantityEl.addEventListener('change', () => this.updatePrice());
      this.quantityEl.addEventListener('input', () => this.updatePrice());
    }
    this.updatePrice();

    // Terug uit Checkout via de terugknop: knop weer bruikbaar maken.
    window.addEventListener('pageshow', () => this.setBusy(false));
  }

  get quantity() {
    const value = parseInt(this.quantityEl ? this.quantityEl.value : '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch (error) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
  }

  updatePrice() {
    if (this.busy || !this.label || this.button.disabled) return;
    const unit = parseInt(this.button.dataset.unitPrice || '0', 10);
    if (!unit) return;
    const text = this.button.dataset.label || 'Bestel nu';
    this.label.textContent = text + ' – ' + this.formatMoney(unit * this.quantity);
  }

  setBusy(state) {
    this.busy = state;
    this.button.disabled = state;
    this.button.setAttribute('aria-busy', state ? 'true' : 'false');
    if (state) {
      this.label.textContent = this.button.dataset.busyText || 'Bezig met bestellen…';
    } else {
      this.updatePrice();
    }
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
    if (this.liveEl) this.liveEl.textContent = message;
  }

  clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const variantId = this.form.querySelector('[data-vr-variant-id]');
    if (!variantId || !variantId.value) {
      this.showError('Kies eerst een variant.');
      return;
    }

    this.clearError();
    this.setBusy(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId.value), quantity: this.quantity }],
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Shopify geeft de reden zelf al in het Nederlands terug wanneer de
        // winkeltaal Nederlands is; anders valt hij terug op onze eigen tekst.
        this.showError(data.description || data.message || 'Toevoegen aan de winkelwagen is niet gelukt. Probeer het opnieuw.');
        this.setBusy(false);
        return;
      }

      if (this.liveEl) this.liveEl.textContent = 'Toegevoegd. Je gaat nu naar afrekenen.';
      window.location.assign(this.checkoutUrl);
    } catch (error) {
      this.showError('Er ging iets mis met de verbinding. Controleer je internet en probeer het opnieuw.');
      this.setBusy(false);
    }
  }
}

if (!customElements.get('vremou-buy')) {
  customElements.define('vremou-buy', VremouBuy);
}
